import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Grid,
  GridProps,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RoundedButton } from "../../../common/components/styled/styled";
import { VariationSelection } from "../../../common/types/apiTypes";
import useTesVariation from "../../../hooks/useTesVariation";
import {
  VariationElement,
  VariationMap,
  VariationSelect,
} from "../types/tesVariationTypes";

export enum CloseAction {
  Accept = "accept",
  Cancel = "cancel",
}

type Props = {
  updateVariation?: (variation: VariationMap) => void;
  elements: VariationElement[];
  onClose?: (action?: CloseAction) => void;
  onChange?: (value: VariationSelection[], isValid: boolean) => void;
  defaultValues: VariationSelection[];
  labelGridProps?: GridProps;
  fieldGridProps?: GridProps;
  fieldProps?: SelectProps; // TODO: add prop variants if different fields needed
  disabled?: boolean;
  reset?: boolean;
  isLoading?: boolean;
};

export const TesVariationForm = ({
  updateVariation,
  elements,
  onClose,
  onChange,
  defaultValues,
  labelGridProps,
  fieldGridProps,
  fieldProps,
  disabled = false,
  reset,
  isLoading,
}: Props) => {
  const {
    control,
    formState: { isDirty },
  } = useForm();
  const { t } = useTranslation("common");

  const {
    variationSelectionMap,
    disabledElements,
    handleVariationChange,
    isValid,
    handleDefaults,
  } = useTesVariation({
    variationDefaults: defaultValues,
    elements,
  });

  // reInitialize hook when elements change
  useEffect(() => {
    handleDefaults({
      variationDefaults: [...variationSelectionMap],
      elements,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements]);

  useEffect(() => {
    if (reset) {
      // cancel changes, reset to defaults
      handleDefaults({
        variationDefaults: [...defaultValues],
        elements,
      });
    }
    // eslint-disable-next-line
  }, [reset]);

  useEffect(() => {
    if (onChange) {
      elements.length > 0 &&
        onChange(Array.from(variationSelectionMap), isValid());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variationSelectionMap]);

  const saveTesVariation = () => {
    if (updateVariation) updateVariation(variationSelectionMap);

    if (onClose) return onClose(CloseAction.Accept);
  };

  const buildFormElements = () => {
    return elements.map((element, i) => {
      switch (element.typeName) {
        case "select": {
          const select = element as VariationSelect;

          return (
            <React.Fragment key={i}>
              <Grid
                container
                item
                xs={3}
                justifyContent="flex-end"
                alignItems="center"
                {...labelGridProps} // pass to override default props
              >
                <Typography textAlign="end" color="textSecondary">
                  {select.id}
                </Typography>
              </Grid>
              <Grid item xs={9} {...fieldGridProps}>
                <Controller
                  rules={{ required: true }}
                  name={select.id}
                  control={control}
                  render={({ field }) => {
                    const fieldValue = variationSelectionMap.get(element.id);
                    return (
                      <Select
                        size="small"
                        fullWidth
                        id={select.id}
                        {...field}
                        value={fieldValue || ""}
                        onChange={(e) => {
                          field.onChange(e);
                          handleVariationChange(
                            element.id,
                            e.target.value as string,
                          );
                        }}
                        disabled={
                          disabled ||
                          Array.from(disabledElements.values()).some((set) =>
                            set.has(select.id),
                          )
                        }
                        {...fieldProps}
                      >
                        {select.options
                          .sort((a, b) => a.id.localeCompare(b.id)) // Sort alphabetically first before mapping
                          .map((option, i) => (
                            <MenuItem key={i} value={option.id}>
                              {option.id}
                            </MenuItem>
                          ))}
                      </Select>
                    );
                  }}
                />
              </Grid>
            </React.Fragment>
          );
        }
        default:
          return null;
      }
    });
  };

  const saveDisabled = isLoading || !isValid() || !isDirty;

  return (
    <>
      {buildFormElements()}
      {!onChange && (
        <Grid item container xs={12}>
          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            pt="16px"
          >
            <RoundedButton
              startIcon={<CloseIcon />}
              variant="outlined"
              onClick={onClose ? () => onClose(CloseAction.Cancel) : undefined}
            >
              {t("cancelShort")}
            </RoundedButton>
            <RoundedButton
              startIcon={<CheckIcon />}
              variant="contained"
              onClick={saveTesVariation}
              disabled={saveDisabled}
            >
              {t("complete")}
            </RoundedButton>
          </Box>
        </Grid>
      )}
    </>
  );
};
